import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthModal } from "../context/AuthModalContext";
import { applySettings, DEFAULT_A11Y, type A11ySettings } from "../context/AccessibilityContext";

function loadFromStorage(): A11ySettings {
  try {
    const saved = localStorage.getItem("accessibilitySettings");
    return saved ? { ...DEFAULT_A11Y, ...JSON.parse(saved) } : { ...DEFAULT_A11Y };
  } catch {
    return { ...DEFAULT_A11Y };
  }
}

function Toggle({ value, onChange, label, desc }: { value: boolean; onChange: (v: boolean) => void; label: string; desc: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div>
        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "3px", maxWidth: "380px" }}>{desc}</div>
      </div>
      <button onClick={() => onChange(!value)}
        style={{ width: "48px", height: "26px", borderRadius: "13px", background: value ? "#C9A84C" : "var(--border)", border: "none", cursor: "pointer", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
        <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "white", position: "absolute", top: "3px", left: value ? "25px" : "3px", transition: "left 0.2s" }}></div>
      </button>
    </div>
  );
}

function RadioGroup({ value, onChange, options, label, desc }: { value: string; onChange: (v: string) => void; options: { value: string; label: string; preview?: string }[]; label: string; desc: string }) {
  return (
    <div style={{ padding: "16px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "12px" }}>{desc}</div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {options.map(opt => (
          <button key={opt.value} onClick={() => onChange(opt.value)}
            style={{ padding: "8px 16px", borderRadius: "8px", border: value === opt.value ? "2px solid #C9A84C" : "1.5px solid var(--border)", background: value === opt.value ? "rgba(201,168,76,0.1)" : "var(--card-bg)", color: value === opt.value ? "#C9A84C" : "var(--text-primary)", fontSize: "13px", fontWeight: value === opt.value ? 700 : 400, cursor: "pointer", transition: "all 0.15s" }}>
            {opt.preview && <span style={{ marginRight: "6px" }}>{opt.preview}</span>}
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AccessibilityPublic() {
  const { t } = useTranslation();
  const { openModal } = useAuthModal();
  const [local, setLocal] = useState<A11ySettings>(loadFromStorage);
  const [saved, setSaved] = useState(false);

  const update = (key: string, value: string | boolean) => {
    const newSettings = { ...local, [key]: value } as A11ySettings;
    setLocal(newSettings);
    applySettings(newSettings); // Apply immediately for live preview
  };

  const handleSave = () => {
    localStorage.setItem("accessibilitySettings", JSON.stringify(local));
    applySettings(local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    setLocal({ ...DEFAULT_A11Y });
    localStorage.setItem("accessibilitySettings", JSON.stringify(DEFAULT_A11Y));
    applySettings(DEFAULT_A11Y);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div style={{ maxWidth: "700px", margin: "0 auto", padding: "32px 24px" }}>

      <div style={{ background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "10px", padding: "12px 18px", marginBottom: "24px", fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.6", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
        <span>
          <strong style={{ color: "var(--text-primary)" }}>Note:</strong> Log in to save your accessibility preferences across all devices. Settings saved here apply to this device only.
        </span>
        <button onClick={openModal} style={{ color: "#C9A84C", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap", padding: 0 }}>
          Sign in →
        </button>
      </div>

      <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "28px 32px", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "20px", borderBottom: "1px solid var(--border)", marginBottom: "8px" }}>
          <div>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.title")}</h2>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "3px" }}>{t("accessibility.subtitle")}</p>
          </div>
          <button onClick={handleReset}
            style={{ fontSize: "12px", color: "var(--text-muted)", background: "transparent", border: "1px solid var(--border)", padding: "6px 12px", borderRadius: "6px", cursor: "pointer" }}>
            {t("accessibility.reset")}
          </button>
        </div>

        <div style={{ marginBottom: "4px", paddingTop: "8px" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.vision")}</div>
          <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{t("accessibility.visionDesc")}</div>
        </div>

        <RadioGroup value={local.font_size} onChange={v => update("font_size", v)}
          label={t("accessibility.fontSize")}
          desc={t("accessibility.fontSizeDesc")}
          options={[
            { value: "small", label: t("accessibility.small"), preview: "A" },
            { value: "medium", label: t("accessibility.medium"), preview: "A" },
            { value: "large", label: t("accessibility.large"), preview: "A" },
            { value: "xlarge", label: t("accessibility.xlarge"), preview: "A" },
          ]} />

        <Toggle value={local.high_contrast} onChange={v => update("high_contrast", v)}
          label={t("accessibility.highContrast")}
          desc={t("accessibility.highContrastDesc")} />

        <RadioGroup value={local.colour_blind_mode} onChange={v => update("colour_blind_mode", v)}
          label={t("accessibility.colourBlind")}
          desc={t("accessibility.colourBlindDesc")}
          options={[
            { value: "none", label: t("accessibility.none") },
            { value: "deuteranopia", label: "Deuteranopia", preview: "🟢" },
            { value: "protanopia", label: "Protanopia", preview: "🔴" },
            { value: "tritanopia", label: "Tritanopia", preview: "🔵" },
          ]} />

        <Toggle value={local.underline_links} onChange={v => update("underline_links", v)}
          label={t("accessibility.underlineLinks")}
          desc={t("accessibility.underlineLinksDesc")} />

        <div style={{ marginTop: "24px", marginBottom: "4px", paddingTop: "8px", borderTop: "0.5px solid var(--border)" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.motion")}</div>
          <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{t("accessibility.motionDesc")}</div>
        </div>

        <Toggle value={local.reduce_motion} onChange={v => update("reduce_motion", v)}
          label={t("accessibility.reduceMotion")}
          desc={t("accessibility.reduceMotionDesc")} />

        <div style={{ marginTop: "24px", marginBottom: "4px", paddingTop: "8px", borderTop: "0.5px solid var(--border)" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.interaction")}</div>
          <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{t("accessibility.interactionDesc")}</div>
        </div>

        <Toggle value={local.read_aloud} onChange={v => update("read_aloud", v)}
          label={t("accessibility.readAloud")}
          desc={t("accessibility.readAloudDesc")} />

        <Toggle value={local.show_hovercards} onChange={v => update("show_hovercards", v)}
          label={t("accessibility.hovercards")}
          desc={t("accessibility.hovercardsDesc")} />

        <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={handleSave}
            style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", padding: "11px 28px", borderRadius: "8px", fontWeight: 700, fontSize: "14px", cursor: "pointer" }}>
            {t("accessibility.saveSettings")}
          </button>
          {saved && <span style={{ color: "var(--success)", fontSize: "13px", fontWeight: 600 }}>{t("accessibility.settingsSaved")}</span>}
        </div>
      </div>

      <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "20px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>Want to sync across devices?</div>
          <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>Create a free account to save your preferences everywhere.</div>
        </div>
        <button onClick={openModal} style={{ background: "#C9A84C", color: "#2C2C2C", padding: "9px 20px", borderRadius: "8px", fontWeight: 700, fontSize: "13px", border: "none", cursor: "pointer", whiteSpace: "nowrap" }}>
          Sign In Free
        </button>
      </div>
    </div>
  );
}
